import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { router } from '@inertiajs/react';
import Layout from '@/layouts/Layout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Heading } from '@/components/ui/heading';
import { JOURNAL_STEPS, JOURNAL_STEP_METADATA, type JournalStep } from '@emovia/journal-flow';
import type {
  JournalCardPayload,
  JournalCardResponsePayload,
  JournalEntry,
  JournalWarmupState,
} from '@domain/journal';
import { useJournalFlow as useWebJournalFlow } from '@/hooks/useJournalFlow';
import { AUTOSAVE_DELAY, MAX_CARDS } from './flowUtils';
import type { FlowStep } from './flowTypes';
import WarmupStep from './components/WarmupStep';
import CardDeckStep from './components/CardDeckStep';
import WritingChoiceStep from './components/WritingChoiceStep';
import WritingStep from './components/WritingStep';
import CompletedStep from './components/CompletedStep';
import CoachPanel from './components/CoachPanel';
import StepProgress from './components/StepProgress';

const stepDefinitions = JOURNAL_STEPS.map((id) => ({
  id: id as FlowStep,
  label: JOURNAL_STEP_METADATA[id].label,
}));

function deriveHand(
  entryHand: JournalCardPayload[] | undefined,
  responses: JournalCardResponsePayload[],
): JournalCardPayload[] {
  if (entryHand && entryHand.length > 0) {
    return entryHand;
  }
  return responses
    .map((response) => response.card)
    .filter((card): card is JournalCardPayload => card != null);
}

const formatDate = (rawDate?: string | null): string => {
  if (!rawDate) {
    return '—';
  }
  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) {
    return rawDate;
  }
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(parsed);
};

export default function Flow({ entry: initialEntry }: { entry: JournalEntry }) {
  const {
    entry,
    warmupDraft,
    writingDraft,
    commitmentDraft,
    alertTherapistDraft,
    step,
    terminated,
    actions,
    advancing,
  } = useWebJournalFlow(initialEntry);

  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isShuffling, setIsShuffling] = useState(false);

  const autosaveTimeout = useRef<NodeJS.Timeout | null>(null);
  const latestDraftRef = useRef(writingDraft);
  const latestCommitmentRef = useRef(commitmentDraft);
  const latestAlertRef = useRef(alertTherapistDraft);

  useEffect(() => {
    latestDraftRef.current = writingDraft;
  }, [writingDraft]);

  useEffect(() => {
    latestCommitmentRef.current = commitmentDraft;
  }, [commitmentDraft]);

  useEffect(() => {
    latestAlertRef.current = alertTherapistDraft;
  }, [alertTherapistDraft]);

  useEffect(() => {
    return () => {
      if (autosaveTimeout.current) {
        clearTimeout(autosaveTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    if (terminated) {
      setErrorMessage('Dati del diario non disponibili. Torno alla lista.');
      router.visit('/patients/journals');
    }
  }, [terminated]);

  const cardResponses = useMemo(() => entry.cardResponses ?? [], [entry.cardResponses]);
  const hand = useMemo(() => deriveHand(entry.hand, cardResponses), [entry.hand, cardResponses]);

  const selectedCards = useMemo(() => hand.filter((card) => card.selected), [hand]);

  const formattedEntryDate = useMemo(() => formatDate(entry.date), [entry.date]);

  const coachMessages = useMemo(() => entry.chat?.messages ?? [], [entry.chat]);

  const scheduleAutosave = useCallback(
    (instant = false) => {
      if (autosaveTimeout.current) {
        clearTimeout(autosaveTimeout.current);
      }

      autosaveTimeout.current = setTimeout(
        () => {
          actions
            .saveWriting({
              content: latestDraftRef.current,
              commitmentText: latestCommitmentRef.current,
              alertTherapist: latestAlertRef.current,
            })
            .then(() => {
              setErrorMessage(null);
            })
            .catch(() => {
              setErrorMessage('Salvataggio automatico non riuscito. Controlla la connessione.');
            });
        },
        instant ? 10 : AUTOSAVE_DELAY,
      );
    },
    [actions],
  );

  const handleWarmupChange = useCallback(
    (key: keyof JournalWarmupState, value: number) => {
      actions.setWarmupDraft({ [key]: value });
    },
    [actions],
  );

  const handleWarmupCommit = useCallback(
    (key: keyof JournalWarmupState, value: number) => {
      const next = {
        ...warmupDraft,
        [key]: value,
      };

      // if (next.moodLevel == null || next.energyLevel == null) {
      //   return;
      // }

      actions
        .updateWarmup({ moodLevel: next.moodLevel, energyLevel: next.energyLevel })
        .then(() => {
          setErrorMessage(null);
        })
        .catch(() => {
          setErrorMessage('Non sono riuscito a salvare il warm-up. Riprova.');
        });
    },
    [actions, warmupDraft],
  );

  const handleWarmupAdvance = useCallback(async () => {
    const mood = warmupDraft.moodLevel ?? null;
    const energy = warmupDraft.energyLevel ?? null;

    if (mood == null || energy == null) {
      setErrorMessage('Imposta sia umore che energia per proseguire.');
      return;
    }

    try {
      await actions.updateWarmup({ moodLevel: mood, energyLevel: energy });
      const advanced = await actions.advance();
      if (!advanced) {
        setErrorMessage('Navigazione del diario non disponibile. Riprova.');
      } else {
        setErrorMessage(null);
      }
    } catch {
      setErrorMessage('Non sono riuscito a salvare il warm-up. Riprova.');
    }
  }, [actions, warmupDraft]);

  const handleCardToggle = useCallback(
    (cardId: number) => {
      actions
        .toggleCardSelection(cardId)
        .then(() => {
          setErrorMessage(null);
        })
        .catch(() => {
          setErrorMessage('Non sono riuscito a registrare la selezione. Riprova.');
        });
    },
    [actions],
  );

  const handleSelectSolo = useCallback(async () => {
    const advanced = await actions.advance('writing');
    if (!advanced) {
      setErrorMessage('Navigazione del diario non disponibile. Riprova.');
    } else {
      setErrorMessage(null);
    }
  }, [actions]);

  const handleResumeDraft = useCallback(async () => {
    const advanced = await actions.advance('writing');
    if (!advanced) {
      setErrorMessage('Navigazione del diario non disponibile. Riprova.');
    }
  }, [actions]);

  const handleDraftChange = useCallback(
    (value: string, isBlurred: boolean) => {
      actions.setWritingDraft(value);
      latestDraftRef.current = value;

      scheduleAutosave(isBlurred);
    },
    [actions, scheduleAutosave],
  );

  const handleCommitmentChange = useCallback(
    (value: string, isBlurred: boolean) => {
      actions.setCommitmentDraft(value);
      latestCommitmentRef.current = value;

      scheduleAutosave(isBlurred);
    },
    [actions, scheduleAutosave],
  );

  const handleAlertToggle = useCallback(
    async (checked: boolean) => {
      actions.setAlertTherapistDraft(checked);
      latestAlertRef.current = checked;

      scheduleAutosave(true);
    },
    [actions, scheduleAutosave],
  );

  const handleShuffleHand = useCallback(async () => {
    if (isShuffling) {
      return;
    }

    setIsShuffling(true);
    setInfoMessage(null);

    try {
      await actions.shuffleHand();
      setErrorMessage(null);
      setInfoMessage('Nuove carte pronte nella tua mano.');
    } catch {
      setErrorMessage('Non sono riuscito a mescolare le carte. Riprova.');
    } finally {
      setIsShuffling(false);
    }
  }, [actions, isShuffling]);

  const handleReturnHome = useCallback(() => {
    router.get('/patients/journals');
  }, []);

  const handleStepSelect = useCallback(
    async (target: FlowStep) => {
      if (target === step) {
        return;
      }
      await actions.advance(target as JournalStep);
    },
    [actions, step],
  );

  const renderCoachStep = (): ReactNode => {
    return (
      <CoachPanel
        key={entry.id}
        entryId={entry.id}
        initialMessages={coachMessages}
        onTranscriptChange={actions.setCoachTranscript}
        onGenerateDraft={actions.advance}
        generatingDraft={advancing}
      />
    );
  };

  let stepContent: ReactNode = null;

  switch (step) {
    case 'warmup':
      stepContent = (
        <WarmupStep
          state={warmupDraft}
          onWarmupChange={handleWarmupChange}
          onWarmupCommit={handleWarmupCommit}
          onContinue={handleWarmupAdvance}
        />
      );
      break;
    case 'cards':
      stepContent = (
        <CardDeckStep
          hand={hand}
          selectedCards={selectedCards}
          maxSelectable={MAX_CARDS}
          isProcessing={advancing}
          isFinishing={advancing}
          isShuffling={isShuffling}
          onToggle={handleCardToggle}
          onShuffle={handleShuffleHand}
          onAdvance={actions.advance}
        />
      );
      break;
    case 'writingChoice':
      stepContent = (
        <WritingChoiceStep
          onSelectSolo={handleSelectSolo}
          onStartCoach={() => actions.advance('coach')}
          onResumeDraft={handleResumeDraft}
          isStartingCoach={advancing}
        />
      );
      break;
    case 'coach':
      stepContent = renderCoachStep();
      break;
    case 'writing':
      stepContent = (
        <WritingStep
          draft={writingDraft}
          writingStatus={'idle'}
          commitment={commitmentDraft}
          alertTherapist={alertTherapistDraft}
          isAdvancing={advancing}
          onDraftChange={handleDraftChange}
          onCommitmentChange={handleCommitmentChange}
          onToggleAlertTherapist={handleAlertToggle}
          onAdvance={actions.advance}
        />
      );
      break;
    case 'completed':
      stepContent = <CompletedStep onReturnHome={handleReturnHome} entry={entry} />;
      break;
    default:
      stepContent = null;
  }

  return (
    <Layout>
      <div className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Heading level={1}>Diario del giorno</Heading>
          <Badge variant="secondary" className="w-fit">
            {formattedEntryDate}
          </Badge>
        </div>

        <StepProgress
          steps={stepDefinitions}
          currentStep={step as FlowStep}
          onSelect={handleStepSelect}
        />

        {infoMessage && (
          <Card className="border border-muted-foreground/20 bg-muted/10 px-4 py-3">
            <p className="text-sm text-muted-foreground">{infoMessage}</p>
          </Card>
        )}
        {errorMessage && (
          <Card className="border border-destructive/30 bg-destructive/10 px-4 py-3">
            <p className="text-sm text-destructive">{errorMessage}</p>
          </Card>
        )}

        {stepContent}
      </div>
    </Layout>
  );
}
